import { getFunctionUrl } from "./functionEndpoints.js";

const GOOGLE_AUTH_FAILURE_STATUSES = new Set([401, 403]);

const buildJsonHeaders = (extraHeaders = {}) => ({
  "Content-Type": "application/json",
  ...extraHeaders,
});

const parseJsonResponse = async (response) => {
  const responseText = await response.text();
  if (!responseText) {
    return null;
  }

  try {
    return JSON.parse(responseText);
  } catch (error) {
    return null;
  }
};

export const createHttpError = (message, status, body = null) => {
  const error = new Error(message);
  error.status = status;
  error.body = body;
  return error;
};

export const isGoogleAuthFailureStatus = (status) =>
  GOOGLE_AUTH_FAILURE_STATUSES.has(Number(status));

export const exchangeGoogleAuthCode = async ({
  code,
  localUserId,
  redirectUri,
}) => {
  const response = await fetch(getFunctionUrl("google-auth-session"), {
    method: "POST",
    headers: buildJsonHeaders({
      "X-Requested-With": "XmlHttpRequest",
    }),
    body: JSON.stringify({
      code,
      local_user_id: localUserId,
      redirect_uri: redirectUri,
    }),
  });
  const body = await parseJsonResponse(response);
  if (!response.ok || !body?.access_token) {
    throw createHttpError(
      body?.error || "Failed to exchange Google authorization code",
      response.status,
      body
    );
  }
  return body;
};

export const refreshGoogleAccessToken = async ({
  localUserId,
  allowMissingRefreshToken = false,
}) => {
  const response = await fetch(getFunctionUrl("refresh-google-access-token"), {
    method: "POST",
    headers: buildJsonHeaders(),
    body: JSON.stringify({
      local_user_id: localUserId,
    }),
  });
  const body = await parseJsonResponse(response);
  if (allowMissingRefreshToken && body?.missing_refresh_token) {
    return body;
  }
  if (!response.ok || !body?.access_token) {
    throw createHttpError(
      body?.error || "Failed to refresh Google access token",
      response.status,
      body
    );
  }
  return body;
};

export const withRefreshedAccessTokenRetry = async (
  spreadsheetAccessData,
  operation,
  options = {}
) => {
  const operationName =
    typeof options?.operationName === "string" && options.operationName.trim()
      ? options.operationName.trim()
      : "Google API operation";
  try {
    return await operation(spreadsheetAccessData);
  } catch (error) {
    if (
      !isGoogleAuthFailureStatus(error?.status) ||
      typeof spreadsheetAccessData?.refreshAccessToken !== "function"
    ) {
      throw error;
    }

    try {
      await spreadsheetAccessData.refreshAccessToken();
      const result = await operation(spreadsheetAccessData);
      console.info(
        `${operationName} recovered after access-token refresh`,
        {
          initialStatus: Number(error?.status) || null,
        }
      );
      return result;
    } catch (refreshError) {
      if (typeof window !== "undefined" && typeof window.location?.reload === "function") {
        window.location.reload();
      }
      throw refreshError;
    }
  }
};
